import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Plus, Check, Trash2, Edit2, X, Calendar, 
  AlertCircle, Clock, User, RefreshCw, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "general", label: "General", color: "bg-gray-500" },
  { value: "follow-up", label: "Client Follow-up", color: "bg-blue-500" },
  { value: "alteration", label: "Alteration", color: "bg-purple-500" },
  { value: "order", label: "Order", color: "bg-green-500" },
  { value: "appointment", label: "Appointment", color: "bg-orange-500" },
];

const PRIORITIES = [
  { value: "high", label: "High", color: "text-red-400 bg-red-500/20" },
  { value: "medium", label: "Medium", color: "text-yellow-400 bg-yellow-500/20" },
  { value: "low", label: "Low", color: "text-green-400 bg-green-500/20" },
];

const AdminTasksPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState("all"); // all, today, completed
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general",
    priority: "medium",
    due_date: format(new Date(), "yyyy-MM-dd"),
    client_name: "",
    is_recurring: false,
    recurrence: null
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchTasks();
    }
  }, [navigate]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tasks`);
      setTasks(response.data);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "general",
      priority: "medium",
      due_date: format(new Date(), "yyyy-MM-dd"),
      client_name: "",
      is_recurring: false,
      recurrence: null
    });
    setEditingTask(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a task title");
      return;
    }

    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, formData);
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/tasks`, formData);
        toast.success("Task created");
      }
      fetchTasks();
      setShowForm(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save task");
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { completed: !task.completed });
      fetchTasks();
      toast.success(task.completed ? "Task marked incomplete" : "Task completed!");
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      fetchTasks();
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      category: task.category || "general",
      priority: task.priority || "medium",
      due_date: task.due_date || "",
      client_name: task.client_name || "",
      is_recurring: task.is_recurring || false,
      recurrence: task.recurrence || null
    });
    setShowForm(true);
  };

  const getFilteredTasks = () => {
    let filtered = [...tasks];
    const today = format(new Date(), "yyyy-MM-dd");

    // Status filter
    if (filter === "today") {
      filtered = filtered.filter(t => !t.completed && (!t.due_date || t.due_date <= today));
    } else if (filter === "completed") {
      filtered = filtered.filter(t => t.completed);
    } else if (filter === "pending") {
      filtered = filtered.filter(t => !t.completed);
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    return filtered;
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.completed) return false;
    return task.due_date < format(new Date(), "yyyy-MM-dd");
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const getPriorityInfo = (priority) => {
    return PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  };

  const pendingCount = tasks.filter(t => !t.completed).length;
  const todayCount = tasks.filter(t => {
    const today = format(new Date(), "yyyy-MM-dd");
    return !t.completed && (!t.due_date || t.due_date <= today);
  }).length;

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="mb-6 -ml-2"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="mono-text text-primary mb-2">TASK MANAGER</p>
              <h1 className="font-serif text-3xl tracking-tight">Daily Tasks</h1>
            </div>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-primary text-primary-foreground hover:bg-foreground"
            >
              <Plus size={16} className="mr-2" />
              New Task
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-card border border-white/10 p-4">
              <p className="text-2xl font-serif text-primary">{todayCount}</p>
              <p className="text-sm text-muted-foreground">Due Today</p>
            </div>
            <div className="bg-card border border-white/10 p-4">
              <p className="text-2xl font-serif">{pendingCount}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
            <div className="bg-card border border-white/10 p-4">
              <p className="text-2xl font-serif text-green-400">{tasks.filter(t => t.completed).length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
            <div className="bg-card border border-white/10 p-4">
              <p className="text-2xl font-serif text-red-400">{tasks.filter(t => isOverdue(t)).length}</p>
              <p className="text-sm text-muted-foreground">Overdue</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Tasks */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <div className="flex gap-2 mr-4">
              {[
                { value: "all", label: "All" },
                { value: "today", label: "Today" },
                { value: "pending", label: "Pending" },
                { value: "completed", label: "Completed" }
              ].map(f => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(f.value)}
                  className={filter === f.value ? "" : "border-white/20"}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-background border border-white/20 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Task List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading tasks...</div>
          ) : getFilteredTasks().length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 size={48} className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No tasks found</p>
              <Button
                onClick={() => { resetForm(); setShowForm(true); }}
                variant="outline"
                className="mt-4 border-white/20"
              >
                Create First Task
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {getFilteredTasks().map((task, index) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.03 }}
                    className={`bg-card border p-4 flex items-start gap-4 group ${
                      task.completed 
                        ? "border-white/5 opacity-60" 
                        : isOverdue(task)
                          ? "border-red-500/30 bg-red-500/5"
                          : "border-white/10"
                    }`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => handleToggleComplete(task)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        task.completed 
                          ? "bg-primary border-primary" 
                          : "border-white/30 hover:border-primary"
                      }`}
                    >
                      {task.completed && <Check size={14} className="text-primary-foreground" />}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className={`font-medium ${task.completed ? "line-through text-muted-foreground" : ""}`}>
                            {task.title}
                          </h3>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(task)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(task.id)}
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${getCategoryInfo(task.category).color}/20 text-white`}>
                          {getCategoryInfo(task.category).label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getPriorityInfo(task.priority).color}`}>
                          {getPriorityInfo(task.priority).label}
                        </span>
                        {task.due_date && (
                          <span className={`text-xs flex items-center gap-1 ${isOverdue(task) ? "text-red-400" : "text-muted-foreground"}`}>
                            <Calendar size={12} />
                            {format(new Date(task.due_date), "MMM d")}
                            {isOverdue(task) && " (Overdue)"}
                          </span>
                        )}
                        {task.client_name && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User size={12} />
                            {task.client_name}
                          </span>
                        )}
                        {task.is_recurring && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <RefreshCw size={12} />
                            {task.recurrence}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Task Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowForm(false); resetForm(); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-card">
                <h2 className="font-serif text-xl">{editingTask ? "Edit Task" : "New Task"}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowForm(false); resetForm(); }}
                >
                  <X size={18} />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Task title"
                    className="bg-background border-white/10"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description..."
                    className="bg-background border-white/10 min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full bg-background border border-white/10 rounded-md px-3 py-2"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full bg-background border border-white/10 rounded-md px-3 py-2"
                    >
                      {PRIORITIES.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Due Date</label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Client Name</label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Optional"
                      className="bg-background border-white/10"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Recurring task</span>
                  </label>
                  {formData.is_recurring && (
                    <select
                      value={formData.recurrence || "daily"}
                      onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                      className="bg-background border border-white/10 rounded-md px-3 py-1.5 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-foreground"
                  >
                    {editingTask ? "Update Task" : "Create Task"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTasksPage;
